import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Fields (custom metadata) — v1 hooks. Migration 0022.
 *
 * Definitions are workspace-wide (GET /api/editor/workspace-settings/fields);
 * defining is owner-only (server gates on ACTIONS.MANAGE_FIELDS, reported via
 * `canManage`). Per-asset values live under /api/editor/videos/:id/fields.
 */

export type FieldType = "select" | "text" | "date";
export type FieldOption = { value: string; color: string | null };

export type FieldDef = {
  id: string;
  label: string;
  type: FieldType;
  options: FieldOption[];
  position: number;
  createdAt?: string | null;
};

export type FieldDefsResponse = {
  fields: FieldDef[];
  canManage: boolean;
  planAllowed: boolean;
};

export type VideoFieldValue = FieldDef & { value: string | null };
export type VideoFieldsResponse = { canEdit: boolean; fields: VideoFieldValue[] };

const FIELDS_KEY = ["workspace-fields"] as const;
const videoFieldsKey = (videoId: string | undefined) => ["video-fields", videoId] as const;

/** GET workspace field definitions (+ canManage). */
export function useFieldDefs(enabled = true) {
  return useQuery<FieldDefsResponse>({
    queryKey: FIELDS_KEY,
    queryFn: () => api.get<FieldDefsResponse>("/editor/workspace-settings/fields"),
    staleTime: 60_000,
    enabled,
  });
}

type CreateBody = { label: string; type: FieldType; options?: FieldOption[] };
export function useCreateFieldDef() {
  const qc = useQueryClient();
  return useMutation<FieldDef, Error, CreateBody>({
    mutationFn: (body) => api.post<FieldDef>("/editor/workspace-settings/fields", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: FIELDS_KEY }),
  });
}

type UpdateBody = { id: string } & Partial<{ label: string; options: FieldOption[]; position: number }>;
export function useUpdateFieldDef() {
  const qc = useQueryClient();
  return useMutation<FieldDef, Error, UpdateBody>({
    mutationFn: ({ id, ...body }) => api.patch<FieldDef>(`/editor/workspace-settings/fields/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: FIELDS_KEY }),
  });
}

export function useDeleteFieldDef() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; id: string }, Error, string>({
    mutationFn: (id) => api.delete<{ ok: boolean; id: string }>(`/editor/workspace-settings/fields/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: FIELDS_KEY }),
  });
}

export function useReorderFieldDefs() {
  const qc = useQueryClient();
  return useMutation<{ fields: FieldDef[] }, Error, string[]>({
    mutationFn: (order) => api.put<{ fields: FieldDef[] }>("/editor/workspace-settings/fields/reorder", { order }),
    onSuccess: (data) =>
      qc.setQueryData<FieldDefsResponse>(FIELDS_KEY, (prev) => (prev ? { ...prev, fields: data.fields } : prev)),
  });
}

/** GET one asset's fields (all active defs + current value). Watch panel. */
export function useVideoFields(videoId: string | undefined, enabled = true) {
  return useQuery<VideoFieldsResponse>({
    queryKey: videoFieldsKey(videoId),
    queryFn: () => api.get<VideoFieldsResponse>(`/editor/videos/${videoId}/fields`),
    enabled: !!videoId && enabled,
    staleTime: 30_000,
  });
}

/** PUT asset field values ({ [fieldDefId]: value|null }). */
export function useSetVideoFields(videoId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<VideoFieldsResponse, Error, Record<string, string | null>>({
    mutationFn: (values) => api.put<VideoFieldsResponse>(`/editor/videos/${videoId}/fields`, { values }),
    onSuccess: (data) => {
      qc.setQueryData(videoFieldsKey(videoId), data);
      // Select chips on the cards come from the project video list — refresh it.
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["videos"] });
    },
  });
}
